/*
 * File: product-toolbar-search.component.ts                                   *
 * Project: catalog-cloud                                                      *
 * File Created: Friday, 3rd June 2022 11:36:52                                *
 * Author: Tomás Muniesa (tomas@cege.es)                                       *
 * -----                                                                       *
 * Last Modified: Thursday, 2nd March 2023 14:40:19                            *
 * Modified By: Tomás Muniesa (tomas@cege.es>)                                 *
 * -----                                                                       *
 * Copyright 2021 - 2023 Nousmedis, CeGe                                       *
 */



/*
 * File: product-toolbar-search.component.ts                                   *
 * Project: catalog-cloud                                                      *
 * File Created: Sunday, 6th March 2022 22:46:11                               *
 * Author: Tomás Muniesa (tomas@cege.es)                                       *
 * -----                                                                       *
 * Last Modified: Wednesday, 1st March 2023 13:10:27                           *
 * Modified By: Tomás Muniesa (tomas@cege.es>)                                 *
 * -----                                                                       *
 * Copyright 2021 - 2022 Nousmedis, CeGe                                       *
 */

import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Product } from '@nousmedis/cc-types';
import { ProductService } from '../../../services/product.service';
import { PageService } from '../../../services/page.service';
import { keyValue } from '../../../../tms-tools/interfaces/key-value';
import { MessageService } from 'primeng/api';

import { FilterOptions } from 'app/modules/catalog-cloud/interfaces/filter-options.interface';
import e from 'cors';
import { FascicleService } from 'app/modules/catalog-cloud/services/fascicle.service';
import { Fascicle } from '@nousmedis/cc-types';
// import { PopInfoService } from '../../../pop-info/services/pop-info-service.service';

interface SortableField {
  label: string;
  value: string;
  group: 'standard' | 'extra';
}

@Component({
  selector: 'app-product-toolbar-search',
  templateUrl: './product-toolbar-search.component.html',
  styleUrls: ['./product-toolbar-search.component.scss'],
})
export class ProductToolbarSearchComponent implements OnInit, OnDestroy {
  @Input() idCatalog: string;
  @Input() idFascicle: string;
  @Input() filter: keyValue[];
  @Input() loading: boolean = true;
  @Input() viewType: string;
  @Input() filterOptions: FilterOptions;
  @Input() viewedProducts: number;
  @Input() sortableFields: SortableField[] = [];

  // @Output() productList: EventEmitter<Product[]> = new EventEmitter<
  //   Product[]
  // >();
  @Output() loadingChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() viewTypeChange: EventEmitter<string> = new EventEmitter<string>();
  @Output() showTooltip: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() sendFilterOptions: EventEmitter<FilterOptions> = new EventEmitter<FilterOptions>();
  @Output() showHistory: EventEmitter<void> = new EventEmitter<void>();

  showInsertedProducts: boolean = false;
  showProductDetailsTooltip: boolean;
  loaded: boolean = false;
  allProducts: number = 0;

  //baseProducts: Product[] = []; //Loaded products from server
  //products: Product[] = [];

  addedProducts: string[] = [];

  // filteredProducts: Product[] = [];

  searchTerms: string[] = [];

  inputValue: string = '';

  private inputSearch$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  productOrder: string = 'asc';
  sortField: string = 'name';

  // addProductsObserver: Subscription;
  // delProductsObserver: Subscription;
  checkScroll: Subscription;

  pageRemoveObserver: Subscription;

  totalProducts: number = 0;


  searchType: string = 'AND';
  searchTypes: any = [];
  fascicle: Fascicle;

  constructor (
    private productService: ProductService,
    private pageService: PageService,
    private messageService: MessageService,
    private trans: TranslateService,
    private fascicleService: FascicleService,
  ) {

    this.searchTypes = [
      { 'name': this.trans.instant('SEARCH.AND_TYPE'), 'value': 'AND', "checked": true },
      { 'name': this.trans.instant('SEARCH.OR_TYPE'), 'value': 'OR', "checked": false }
    ];

  }

  async ngOnInit(): Promise<void> {
    this.productService.isShowProductDetails().subscribe((show) => {
      this.showTooltip.emit(show);
      if(!show) this.showProductDetailsTooltip = false;
    });

    this.inputSearch$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(value => {
      this.productService.offsetSearch = 0;
      this.productService.setSearch(
        this.idCatalog,
        this.filter,
        value.trim().length > 0 ? [...this.searchTerms, value.trim()] : this.searchTerms,
        this.productOrder,
        this.filterOptions.searchType as 'AND' | 'OR'
      );
      this.productService.postSearchProducts(this.fascicle.tags, this.fascicle.categories);
    });

    this.fascicle = await firstValueFrom(this.fascicleService.get(this.idCatalog, this.idFascicle));
    this.setTotalProducts();
    this.searchType = this.filterOptions.searchType;
    this.showInsertedProducts = this.filterOptions.viewInsertedProducts;
    this.showProductDetailsTooltip = this.filterOptions.showWindow;
    this.viewType = this.filterOptions.viewType;
    this.sortField = this.filterOptions.sortField ?? 'name';
    this.changeOrder(this.filterOptions.sort);
    this.loaded = true;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async setTotalProducts() {
    let categories = this.filter.filter((f) => f.id == 'categories')[0];
    let tags = this.filter.filter((f) => f.id == 'tags')[0];
    await this.productService
      .countProducts(this.idCatalog, categories.value, tags.value)
      .subscribe((data) => {
        this.allProducts = data;
        
        this.initToolbarSearch();
      });
  }

  onInputChange(value: string): void {
    this.inputValue = value;
    this.inputSearch$.next(value);
  }

  clearAllFilters(): void {
    this.searchTerms = [];
    this.inputValue = '';
    this.productService.offsetSearch = 0;
    this.searchProducts();
  }

  addFilter(e: any) {
    //this.products = [];
    this.productService.offsetSearch = 0;
    if (e.value.trim().length > 0) {
      this.searchTerms.push(e.value);
      e.value = '';
      this.searchProducts();
    }
  }

  changeLoading(isLoading: boolean) {
    this.loading = isLoading;
    this.loadingChange.emit(this.loading);
  }

  changeViewType(type: any) {
    this.viewTypeChange.emit(type);
    this.filterOptions.viewType = type;
    this.sendFilterOptions.emit(this.filterOptions);
  }

  initToolbarSearch() {
    // this.productService
    //   .getAddedProducts(this.idCatalog, this.idFascicle)
    //   .subscribe((addedProducts) => {
    //     this.addedProducts = addedProducts;
    //     //this.filterProducts();
    //     this.changeLoading(false);
    //     this.setProductHandlers();
    //     //this.loadProducts();
    //   });
  }

  removeSearchItem(searchTerm: string) {
    let index = this.searchTerms.indexOf(searchTerm);
    //this.products = [];
    this.productService.offsetSearch = 0;
    this.searchTerms.splice(index, 1);
    this.searchProducts();
  }

  // filterProducts() {
  //   
  //   if (!this.showInsertedProducts) {
  //   //   if (this.addedProducts && this.addedProducts.length > 0) {
  //   //     const filteredProducts = this.products.filter(
  //   //       (prod) => !this.addedProducts.includes(prod.id)
  //   //     );
  //   //     this.productService.productList$.next(filteredProducts);
  //   //   }
  //   //   else {
  //   //     this.productService.productList$.next(this.products);
  //   //   }

  //   // } else {
  //   //   this.productService.productList$.next(this.products);
  //     // this.filteredProducts = this.products;
  //   }
  //   // let properties = this.productService.getAllProperties(this.filteredProducts);
  //   // 

  //   //this.productList.emit(this.filteredProducts);
  //   this.productService.orderHandler.next(this.productOrder);
  //   this.changeLoading(false);
  // }

  setProductHandlers() {

    // this.addProductsObserver = this.productService.addProduct$.subscribe(
    //   (prod) => {
    //     this.addedProducts.push(prod);
    //     this.filterProducts();
    //   }
    // );

    // this.delProductsObserver = this.productService.removeProduct$.subscribe(
    //   (prod) => {
    //     let index = this.addedProducts.indexOf(prod);
    //     if (index >= 0) this.addedProducts.splice(index, 1);
    //     this.filterProducts();
    //   }
    // );

    this.pageRemoveObserver = this.pageService.returnItemToToolbar$.subscribe(
      (prod) => {
        let index = this.addedProducts.indexOf(prod);
        if (index >= 0) this.addedProducts.splice(index, 1);
        //this.filterProducts();
      }
    );

    this.productService.showProductDetailsWindow$.subscribe((show) => {
      this.showProductDetailsTooltip = show;
    });

    
  }

  changeOrder(order: any) {
    // Si order ya viene con formato 'field:direction', úsalo directamente
    // Si no, combinarlo con el sortField actual
    const fullOrder = order.includes(':') ? order : `${this.sortField}:${order}`;
    const direction = fullOrder.split(':')[1] as 'asc' | 'desc';
    this.productOrder = direction;
    this.filterOptions.sort = direction;
    // El sort es client-side: solo emite al orderHandler para que el padre
    // ordene la lista en memoria. NO llama searchProducts() para no sobreescribir
    // la lista ordenada con una respuesta nueva del servidor.
    this.productService.orderHandler.next(fullOrder);
    this.sendFilterOptions.emit(this.filterOptions);
  }

  changeSortField(field: string): void {
    this.sortField = field;
    this.filterOptions.sortField = field as any;
    this.changeOrder(`${field}:${this.productOrder}`);
  }

  searchProducts() {
    this.productService.setSearch(this.idCatalog, this.filter, this.searchTerms, this.productOrder,this.filterOptions.searchType as 'AND' | 'OR');
    this.productService.postSearchProducts(this.fascicle.tags, this.fascicle.categories);
  }

  toggleShowInsertedProducts() {
    this.showInsertedProducts = this.showInsertedProducts;
    //this.filterProducts();
    //Añadir si esta checked o no
    
    this.filterOptions.viewInsertedProducts = this.showInsertedProducts;
    this.searchProducts();
    this.sendFilterOptions.emit(this.filterOptions);
  }

  toogleShowProductDetails() {
    this.productService.setShowProductsDetails(this.showProductDetailsTooltip)
    this.filterOptions.showWindow = this.showProductDetailsTooltip;
    this.sendFilterOptions.emit(this.filterOptions);

  }

  changeSearchType(e: 'AND' | 'OR') {
    this.searchType = e;
    this.filterOptions.searchType = e;
    this.productService.setSearch(
      this.idCatalog,
      this.filter,
      this.searchTerms,
      this.productOrder,
      this.searchType as 'AND' | 'OR'
    )
    this.sendFilterOptions.emit(this.filterOptions);
    this.searchProducts();

  }
}
